import angular from 'angular';
import eventsTemplate from './events.template.html';

/*****************************************************************************
 * Constant: ET_LINK */
/**
 * @ngdoc     component
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.constant:ET_LINK
 * @description
 * Builds and returns the link to Event Tracker. The link cannot be hard coded
 * because the user might be on local, staging, or on the production server.
 *****************************************************************************/
/* @ngInject */ export function ET_LINK() {
  return window.location.href.split('/(S(')[0] + '/et';
}

/*******************************************************************************
 * Filter: openEventFilter
 * @ngdoc     component
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.filter:openEventFilter
 * @description
 * Returns a filter function to be used with the Event Type column. Displays the
 * correct string `Ticketed Event` or `Open Event`.
 *******************************************************************************/
/* @ngInject */ export function openEventFilter(Tools) {
  return function (input) {
    return !input ? 'Ticketed Event' : 'Open Event';
  };
}

/*****************************************************************************
 * Component: events */
/**
 * @ngdoc     component
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.component:events
 * @param    {Array}    list         List of Events.
 * @param    {Boolean}  oldEvents    Boolean indication that the events in the
 *                                   list have been ended.
 * @param    {Boolean}  showButtons  True=Show control buttons at the bottom
 *                                   of the component.
 * @description
 * The main Events app component.
 *****************************************************************************/
export const eventsComponent = {
  bindings: { list: '<', oldEvents: '<', showButtons: '<' },
  controller: 'EventsCtrl',
  template: eventsTemplate
}

/*****************************************************************************
 * Controller: EventsCtrl */
/**
 * @ngdoc     controller
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:EventsCtrl
 * @description
 * Controller for the {@link events}.
 *****************************************************************************/
/* @ngInject */ export function EventsCtrlController($injector, $scope) {
  var $ctrl = this;
  var $utils = {};

  $ctrl.dgEvents = $injector.invoke(buildGridOptions, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.addEvent = $injector.invoke(addEvent, null, {
    $ctrl: $ctrl,
    $utils: $utils
  });
  $ctrl.changeEvent = $injector.invoke(changeEvent, null, {
    $ctrl: $ctrl,
    $utils: $utils
  });
  $ctrl.copyLink = $injector.invoke(copyLink, null, {});
  $ctrl.dropEvent = $injector.invoke(dropEvent, null, { $utils: $utils });
  $ctrl.endEvent = $injector.invoke(endEvent, null, {
    $ctrl: $ctrl,
    $utils: $utils
  });
  $ctrl.generateReports = $injector.invoke(generateReports, null, {
    $ctrl: $ctrl
  });
  $ctrl.$onInit = $injector.invoke($onInit, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.launchTracker = $injector.invoke(launchTracker, null, {});
  $ctrl.resetEvent = $injector.invoke(resetEvent, null, { $ctrl: $ctrl });
  $ctrl.setView = $injector.invoke(setView, null, { $ctrl: $ctrl });

  $utils.loadEventsList = $injector.invoke(loadEventsList, null, {
    $ctrl: $ctrl
  });
}

/***************************************************************************
 * addEvent */
/**
 * @ngdoc     controller
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:addEvent
 * @description
 * Click handler for the `Add Event` button, and the `Change  Event` button
 * in the grid. Double click handler on the grid items. Displays the dialog
 * to create or modify an event.
 ***************************************************************************/
/* @ngInject */ function addEvent(
  $ctrl,
  $utils,
  AddChangeEventDialog,
  Alert,
  BusyScreen,
  SelectEventTypeDialog,
  Tools
) {
  /*---------------------*/
  /* Dialog active flag. */
  /*---------------------*/
  var dialogActive = false;

  return function () {
    /*--------------------------------------*/
    /* Exit if the dialog is already active */
    /* or if user is viewing old events.    */
    /*--------------------------------------*/
    if (dialogActive || $ctrl.oldEvents) return;

    /*--------------------------------------------------------*/
    /* Promp user to select event type before creating event. */
    /*--------------------------------------------------------*/
    dialogActive = true;
    SelectEventTypeDialog.show()
      .then(function (eventTypeOpts) {
        AddChangeEventDialog.show(0, eventTypeOpts).then(function (resp) {
          $utils.loadEventsList();

          /*------------------------------------------------------------------*/
          /* The event was saved and needs to be reopened to upload students. */
          /*------------------------------------------------------------------*/
          if (resp.reopenDialog) {
            Alert.info('Reloading event...', 'Please Wait');
            $ctrl.changeEvent(resp.id);
          }
        });
      })
      .finally(function () {
        dialogActive = false;
      });
  };
}

/*****************************************************************************
 * buildGridOptions */
/**
 * @ngdoc     function
 * @name      buildGridOptions
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:addChangeEvent
 * @returns  {object} uiGrid definition object
 * @description
 * Generates the uiGrid definition object to be used with the main grid.
 *****************************************************************************/
/* @ngInject */ function buildGridOptions($ctrl, $scope, uiGridConstants) {
  /*----------------------------------------------------*/
  /* Template to add double click functionality to each */
  /* grid row. Begins editing the double clicked event. */
  /*----------------------------------------------------*/
  var dblClickTemplate =
    '<div ng-dblclick="grid.appScope.$ctrl.changeEvent(row.entity.id)" ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" class="ui-grid-cell" ui-grid-cell></div>';

  /*---------------------------------------------------------------*/
  /* Alt text label for the change button in the actions template. */
  /*---------------------------------------------------------------*/
  var changeLbl = $ctrl.oldEvents ? 'Event Details' : 'Change Event';

  /*-------------------------------------------------*/
  /* Template for the actions column with icons to   */
  /* manage attendees, reset, edit and delete event. */
  /*-------------------------------------------------*/
  var actionsCellTemplate =
    '<div class="ui-grid-cell-contents ng-binding ng-scope"> ' +
    (!$ctrl.oldEvents
      ? '<span class="glyphicon glyphicon-refresh csm-grid-space-target pointer" title="Reset Event"     ng-click="grid.appScope.$ctrl.resetEvent(row.entity)"></span> &nbsp;'
      : '') +
    ($ctrl.oldEvents
      ? '<span class="glyphicon glyphicon-ok      csm-grid-space-target pointer" title="Un-End Event"    ng-click="grid.appScope.$ctrl.endEvent(row.entity)"></span> &nbsp;'
      : '') +
    (!$ctrl.oldEvents
      ? '<span class="glyphicon glyphicon-remove  csm-grid-space-target pointer" title="End Event"       ng-click="grid.appScope.$ctrl.endEvent(row.entity)"></span> &nbsp;'
      : '') +
    '<span class="glyphicon glyphicon-edit    csm-grid-space-target pointer" title="' +
    changeLbl +
    '"   ng-click="grid.appScope.$ctrl.changeEvent(row.entity.id)"></span> &nbsp;' +
    (!$ctrl.oldEvents
      ? '<span class="glyphicon glyphicon-trash   csm-grid-space-target pointer" title="Remove Event"    ng-click="grid.appScope.$ctrl.dropEvent(row.entity)"></span> &nbsp;'
      : '') +
    '</div>';

  return {
    appScope: $scope,
    enableColumnMenus: false,
    enableFiltering: true,
    enableFullRowSelection: true,
    enableRowHeaderSelection: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    rowTemplate: dblClickTemplate,
    multiSelect: false,
    columnDefs: [
      {
        cellFilter: 'date:"MM/dd/yyyy"',
        filterCellFiltered: true,
        type: 'date',
        field: 'date',
        width: 110
      },
      { field: 'name' },
      { field: 'location' },
      {
        name: 'Event Type',
        field: 'openEvent',
        cellFilter: 'openEventFilter',
        width: 200,
        filter: {
          term: null,
          type: uiGridConstants.filter.SELECT,
          selectOptions: [
            { value: null, label: 'ALL' },
            { value: true, label: 'Open Event' },
            { value: false, label: 'Ticketed Event' }
          ]
        }
      },
      {
        cellTemplate: actionsCellTemplate,
        enableFiltering: false,
        enableSorting: false,
        field: 'actions',
        width: $ctrl.oldEvents ? 90 : 100,
        displayName: 'Actions',
        pinnedRight: true
      }
    ]
  }
}

/***************************************************************************
 * changeEvent */
/**
 * @ngdoc     controller
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:changeEvent
 * @param    id    Event ID to manage, null/0 if new.
 * @description
 * Click handler for the `Change  Event` button in the grid and double click
 * action on the grid items. Displays the dialog to modify an event.
 ***************************************************************************/
/* @ngInject */ function changeEvent($ctrl, $utils, AddChangeEventDialog, Alert, BusyScreen) {
  return function (id) {
    AddChangeEventDialog.show(id, null, $ctrl.oldEvents).then(
      $utils.loadEventsList
    );
  };
}

/***************************************************************************
 * copyLink */
/**
 * @ngdoc     function
 * @name      copyLink
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:copyLink
 * @description
 * Click handler for the 'Copy Link' button. Copies the Event Tracker link
 * to the user's clipboard.
 ***************************************************************************/
/* @ngInject */ function copyLink($location, Alert, ET_LINK, UserAgent) {
  return function () {
    /*------------------------------------------------------------*/
    /* For IE, use browser API to copy the link to the clipboard. */
    /*------------------------------------------------------------*/
    if (UserAgent.isIE()) window.clipboardData.setData('Text', ET_LINK);
    else {
      /*-------------------------------------------------------*/
      /* For all others, create an input element with the link */
      /* and display it off the screen, copy the link to the   */
      /* clipboard and remove the element from the DOM. The    */
      /* element must be an input in order to copy the text.   */
      /*-------------------------------------------------------*/
      var angInput = angular.element(
        "<input type='text' style='position:absolute'>"
      );
      angInput.val(ET_LINK);
      angInput.css(
        'right',
        '-' + (Math.max(window.innerWidth, window.innerHeight) + 1000) + 'px'
      );

      var body = document.querySelector('body');
      body.appendChild(angInput[0]);
      angInput[0].select();
      document.execCommand('Copy');
      body.removeChild(angInput[0]);
    }
    Alert.success(
      'The Event Tracker URL has been copied onto your clipboard',
      'Link Copied'
    );
  };
}

/***************************************************************************
 * dropEvent */
/**
 * @ngdoc     function
 * @name      copyLink
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:dropEvent
 * @param  event  Event to remove.
 * @description
 * Drops the provided event from the database.
 ***************************************************************************/
/* @ngInject */ function dropEvent($utils, Alert, BusyScreen, Events) {
  return function (event) {
    /*--------------------------*/
    /* Nothing to remove, exit. */
    /*--------------------------*/
    if (!event || !event.id) return;

    /*---------------------------*/
    /* Confirm and remove event. */
    /*---------------------------*/
    Alert.confirm(
      'Are you sure you wish to remove ' + event.name + '?',
      'Confirm Removal'
    ).then(function () {
      BusyScreen.setBusy(true);
      Events.drop(event.id)
        .then($utils.loadEventsList)
        .then(function (resp) {
          BusyScreen.setBusy(false);
          Alert.success((event.name || 'Event') + ' has been removed.');
        });
    });
  };
}

/***************************************************************************
 * endEvent */
/**
 * @ngdoc     function
 * @name      endEvent
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:endEvent
 * @param  event  Event to modify.
 * @description
 * Ends or undo the end on the given event.
 ***************************************************************************/
/* @ngInject */ function endEvent($ctrl, $utils, Alert, Events) {
  return function (event) {
    var unend = !!$ctrl.oldEvents;
    var title = unend ? 'Undo End Event' : 'End Event';
    var msg = unend
      ? 'Do you undo the end event for ' + event.name + '?'
      : 'Do you want to mark the event ' + event.name + ' as ended?';

    Alert.confirm(msg, title).then(function () {
      /*------------------------------------------------------*/
      /* When displaying past events, if end event is called, */
      /* the flag is passed to unend the event. If displaying */
      /* current events, the oldEvents flag is false.         */
      /*------------------------------------------------------*/
      Events.endEvent(event.id, unend)
        .then($utils.loadEventsList)
        .then(function () {
          Alert.success('Event has been ended');
        });
    });
  };
}

/***************************************************************************
 * generateReports */
/**
 * @ngdoc     function
 * @name      copyLink
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:generateReports
 * @description
 * Display dialog to generate reports.
 ***************************************************************************/
/* @ngInject */ function generateReports($ctrl, ReportsDialog) {
  return function () {
    ReportsDialog.show($ctrl.dgEvents.data).then(angular.noop);
  };
}

/***************************************************************************
 * launchTracker */
/**
 * @ngdoc     function
 * @name      copyLink
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:launchTracker
 * @description
 * Click handler for the 'Launch Event Tracker' button. Opens the Event
 * Tracker in a new browser tab.
 ***************************************************************************/
/* @ngInject */ function launchTracker($window, ET_LINK) {
  return function () {
    $window.open(ET_LINK);
  };
}

/***************************************************************************
 * loadEventsList */
/**
 * @ngdoc     function
 * @name      copyLink
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:launchTracker
 * @return    Promise that resolves after the grid has been populated.
 * @description
 * Loads the list of events and populates the grid for the current view.
 ***************************************************************************/
/* @ngInject */ function loadEventsList($ctrl, BusyScreen, Events) {
  return function () {
    return BusyScreen.promise(
      Events.getList().then(function (resp) {
        $ctrl.dgEvents.data = resp.filter(function (e) {
          return !!e.inactive == !!$ctrl.oldEvents;
        });
      })
    );
  };
}

/*****************************************************************************
 * $onInit */
/**
 * @ngdoc     method
 * @name      $onInit
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:$onInit
 * @description
 * Called by angular when the component is ready. Initializes the component.
 *****************************************************************************/
/* @ngInject */ function $onInit($ctrl, $scope, UserAgent) {
  return function () {
    /*---------------------------*/
    /* Is user is on iOS device. */
    /*---------------------------*/
    $ctrl.iOSDevice = UserAgent.isIOSDevice();

    /*--------------------------------------------------*/
    /* Model for the List/Calendar toggle button        */
    /* group. Indicates which view is currently active. */
    /*--------------------------------------------------*/
    $ctrl.viewMode = { list: true, calendar: false };

    /*-------------------------------*/
    /* Calendar events dataprovider. */
    /*-------------------------------*/
    var eventList = [];

    /*------------------------------*/
    /* Angular UI calendar options. */
    /*------------------------------*/
    $ctrl.calOptions = {
      config: {
        height: 450,
        editable: false,
        eventClick: $ctrl.eventClick,
        header: {
          left: 'title',
          center: '',
          right: 'today prev,next'
        }
      }
    };

    /*------------------------------------------*/
    /* Angular UI Calendar Events dataprovider. */
    /*------------------------------------------*/
    $ctrl.calData = [{ events: eventList }];

    /*------------------------------------------------*/
    /* Watch to update the Angular UI Calendar events */
    /* dataprovider when the list of events changes.  */
    /*------------------------------------------------*/
    $scope.$watch('$ctrl.list', function (events) {
      eventList.length = 0;
      angular.forEach(events, function (e) {
        /*-------------------------------------------------------*/
        /* Events must have dates to be displayed on a calendar. */
        /*-------------------------------------------------------*/
        if (e.date)
          eventList.push({
            id: e.id,
            title: e.name,
            start: e.date,
            location: e.location,
            allDay: true
          });
      });
    });

    /*-------------------------------*/
    /* Populate grid in `List View`. */
    /*-------------------------------*/
    $ctrl.dgEvents.data = $ctrl.list;
  };
}

/***************************************************************************
 * resetEvent */
/**
 * @ngdoc     method
 * @name      resetEvent
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:resetEvent
 * @param  event  Event to reset.
 * @description
 * Handles reseting the event to a just created state, deleting all checkins.
 ***************************************************************************/
/* @ngInject */ function resetEvent($ctrl, Alert, BusyScreen, Events) {
  return function (event) {
    /*-------------------------------------------------*/
    /* Verify user is allowed to reset/restart events. */
    /*-------------------------------------------------*/
    if (!!$ctrl.oldEvents) return;

    Alert.confirm(
      'This will clear all check-in and check-out activity for "' +
        event.name +
        '". ' +
        'This action can not be undone.',
      'Reset Event'
    ).then(function () {
      BusyScreen.promise(
        Events.reset(event.id).then(function () {
          Alert.success('Reset Successful');
        })
      );
    });
  };
}

/***************************************************************************
 * setView */
/**
 * @ngdoc     method
 * @name      setView
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.controller:setView
 * @param  mode  Boolean indication of mode that is active. True=List Mode,
 *               false=Calendar Mode.
 * @description
 * Updates the viewMode object to reflect which mode is currently active.
 * Ignored when displaying past events.
 ***************************************************************************/
/* @ngInject */ function setView($ctrl) {
  return function (mode) {
    /*-------------------------------------*/
    /* List/Calendar toggle buttons not    */
    /* available when viewing past events. */
    /*-------------------------------------*/
    if ($ctrl.oldEvents) return;

    $ctrl.viewMode.list = mode;
    $ctrl.viewMode.calendar = !mode;
  };
}
